import type * as React from 'react'
import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import {
  AlertDialog,
  Button,
  DropdownMenu,
  toast,
  TrashIcon,
} from '@buffer-mono/popcorn'

import { usePostData, useTrackActionClicked } from '../PostCardContext'
import { useDeletePost } from '../useDeletePost'

export function DeletePostMenuItem({
  onDeleted,
}: {
  /** Called after a successful delete, e.g. to navigate away from a route
   * that identifies the post by id. */
  onDeleted?: () => void
} = {}): JSX.Element | null {
  const { id, allowedActions } = usePostData()
  const [deletePost, { loading }] = useDeletePost()
  const [dialogOpen, setDialogOpen] = useState(false)
  const trackPostAction = useTrackActionClicked()

  const handleConfirmDelete: React.MouseEventHandler = useCallback(
    async (e) => {
      e.preventDefault()

      const { success, error } = await deletePost({ variables: { id } })
      trackPostAction('deletePost-1')

      if (!success) {
        toast.error(`Failed to delete post: ${error.message}`)
        return
      }

      setDialogOpen(false)
      toast.success('Post was deleted')
      onDeleted?.()
    },
    [deletePost, id, trackPostAction, onDeleted],
  )

  if (!allowedActions.includes('deletePost')) {
    return null
  }

  return (
    <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <AlertDialog.Trigger>
        <DropdownMenu.Item
          variant="critical"
          // preventDefault is necessary so that dialog can pass onClick
          onSelect={(e): void => {
            e.preventDefault()
          }}
        >
          <TrashIcon />
          Delete Post
        </DropdownMenu.Item>
      </AlertDialog.Trigger>
      <AlertDialog.Content>
        <AlertDialog.Title>Delete This Post?</AlertDialog.Title>
        <AlertDialog.Description>
          This post will be permanently deleted. This action cannot be undone.
        </AlertDialog.Description>
        <AlertDialog.Actions>
          <AlertDialog.Cancel>
            <Button variant="tertiary">Cancel</Button>
          </AlertDialog.Cancel>
          <AlertDialog.Action>
            <Button
              variant="critical"
              loading={loading}
              onClick={handleConfirmDelete}
            >
              {loading ? 'Deleting...' : 'Delete Post'}
            </Button>
          </AlertDialog.Action>
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}
